import { RightOutlined } from '@ant-design/icons'
import { Button, Typography } from 'antd'
import cls from 'classnames'
import { round } from 'lodash'
import React from 'react'
import { Draggable, DraggableProvided, Droppable } from 'react-beautiful-dnd'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { DepartmentName, MosaicComponent, UserAvatar, getPointPermission } from '@/components'
import { ReportRisk } from '@/modules/project/project-detail/project-report'
import { routes } from '@/router'
import {
  PAGE_RESOURCE_POINTS_ENUM,
  ReportConfigurationModule,
  ReportSnapshot as ReportSnapshotSchema,
  ResourceType,
} from '@/schemas'

import { ReportExtras } from './report-extra.component'
import { SnapshotsRelationCreator } from './snapshots-relation-creator.component'

const { Text } = Typography

export interface Node<T> {
  id: string
  data: T
  depth: number
  children: Node<T>[]
  descendants: () => Node<T>[]
}

interface ReportSnapshotsProps {
  selfId: string
  reportId: number
  snapshots: Node<ReportSnapshotSchema>[]
  refresh: () => void
  editable: boolean
  isShowKR: boolean
  isFull: boolean
  expandSnapshotIds: string[]
}

interface ReportSnapshotProps extends ReportSnapshotsProps {
  index: number
  snapshot: Node<ReportSnapshotSchema>
  childCollapsed: boolean
  changeCollapsed: (id: string) => void
}

const prefixImgEnum = {
  0: 'https://web-cdn.agora.io/pmo/icons/project.png',
  1: 'https://web-cdn.agora.io/pmo/icons/portfolio.png',
  2: 'https://web-cdn.agora.io/pmo/icons/target.png',
  3: 'https://web-cdn.agora.io/pmo/icons/team.png',
}

const ReportSnapshot: React.FC<ReportSnapshotProps> = (props) => {
  const { t } = useTranslation()
  const {
    index,
    selfId,
    reportId,
    snapshot,
    refresh,
    editable,
    childCollapsed,
    changeCollapsed,
    isShowKR,
    isFull,
    expandSnapshotIds,
  } = props
  const s = snapshot.data
  const { children = [] } = snapshot
  const { editable: editableContent, canRead } = getPointPermission(
    PAGE_RESOURCE_POINTS_ENUM.周报_内容,
    s.permissions ?? s.report?.permissions
  )

  const [extraModules, setExtraModules] = React.useState<ReportConfigurationModule | undefined>(undefined)

  React.useEffect(() => {
    if (s.config) {
      setExtraModules(s.config.modules.find((m) => m.isExtra))
    }
  }, [s.config])

  const name = {
    [ResourceType.STAGE]: s.name,
    [ResourceType.PROJECT]: (
      <Link to={routes.project.page(s.selfId)} target='__blank' onClick={(e) => e.stopPropagation()}>
        {s.name}
      </Link>
    ),
    [ResourceType.PORTFOLIO]: (
      <Link to={routes.portfolio.page(s.selfId)} target='__blank' onClick={(e) => e.stopPropagation()}>
        {s.name}
      </Link>
    ),
    [ResourceType.OBJECTIVE]: s.name,
    [ResourceType.TEAM]: (
      <Link
        to={routes.team.home({ departmentId: s.departmentId, tab: 'report' })}
        target='__blank'
        onClick={(e) => e.stopPropagation()}
      >
        <DepartmentName canRead={true} departmentId={s.departmentId} />
      </Link>
    ),
    [ResourceType.CUSTOMER]: (
      <Link to={routes.customer.detail(s.selfId)} target='__blank' onClick={(e) => e.stopPropagation()}>
        {getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_名称, s.permissions ?? s.report.permissions).canRead ? (
          s.name
        ) : (
          <MosaicComponent content={s.name} />
        )}
      </Link>
    ),
    [ResourceType.PRODUCTION]: (
      <Link to={routes.production.detail(s.selfId)} target='__blank' onClick={(e) => e.stopPropagation()}>
        {s.name}
      </Link>
    ),
    [ResourceType.DEVLOP]: (
      <Link to={routes.devlop.detail(s.selfId)} target='__blank' onClick={(e) => e.stopPropagation()}>
        {s.name}
      </Link>
    ),
  }[s.type]

  const edit = editable && (
    <SnapshotsRelationCreator
      rootSelfId={selfId}
      selfId={s.selfId}
      snapshot={s}
      depth={snapshot.depth}
      onSuccess={refresh}
      disabledSelfIds={children.map((d) => d.data.selfId)}
    >
      <Button
        size='small'
        type='link'
        className='text-xs opacity-0 transition ant-collapse-header-hover !px-0 group-hover:opacity-100'
        onClick={(e) => e.stopPropagation()}
      >
        {t('edit')}
      </Button>
    </SnapshotsRelationCreator>
  )

  const header = (provided: DraggableProvided) => (
    <div className='report-snapshot-header'>
      <div className='flex justify-between ant-collapse-header' {...provided.dragHandleProps}>
        <div className='flex mr-16px items-center'>
          <div className='font-bold mr-8px'>
            <img className='h-24px w-24px' src={prefixImgEnum[s.type]} alt='' />
          </div>
          <div className='group'>
            <span className='font-bold mr-8px'>{name}</span>
            <span onClick={(e) => e.stopPropagation()}>{edit}</span>
          </div>
        </div>
        <div className='flex space-x-8px flex-shrink-0 min-w-200px items-center justify-end'>
          {/* 新增项目当前阶段，取消展示状态 */}
          {getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_名称, s.permissions ?? s.report.permissions).canRead ? (
            s['currentStage'] && (
              <span className=' bg-light-400 rounded-2px py-2px px-6px inline-block'>
                {t('阶段')}:{s['currentStage'].name}
              </span>
            )
          ) : (
            <MosaicComponent />
          )}
          {getPointPermission(PAGE_RESOURCE_POINTS_ENUM.周报_内容, s.permissions ?? s.report.permissions).canRead ? (
            <ReportRisk report={s.report} disabled={!editable || !editableContent} />
          ) : (
            <MosaicComponent />
          )}
          {getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_进度, s.permissions ?? s.report.permissions).canRead ? (
            // t('进行中') t('暂停') t('已结项') t('未开始') t('计划中') t('结项中')
            s.type !== ResourceType.TEAM && (
              <Text type='secondary' className='text-xs children'>
                <span>
                  {t('progress')}
                  <span className='text-right w-40px inline-block'>{round(s.progress, 0)}%</span>
                </span>
              </Text>
            )
          ) : (
            <MosaicComponent />
          )}
          {getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_负责人, s.permissions ?? s.report.permissions).canRead ||
          s.type === ResourceType.PRODUCTION ? (
            <UserAvatar email={s.email} className='h-20px w-20px' />
          ) : (
            <MosaicComponent />
          )}
        </div>
      </div>
      {isShowKR && s.type === ResourceType.OBJECTIVE && (
        <ul className='mt-8px'>{s['krs'] && s['krs'].map((kr: string) => <li key={kr}>{kr}</li>)}</ul>
      )}
    </div>
  )

  const content = (
    <div
      className={cls(
        'ant-collapse-content mb-8px border-0 border-l-1 border-solid border-light-500',
        childCollapsed ? 'hidden' : '',
        snapshot.depth > 1 ? ' ml-32px' : 'ml-20px'
      )}
    >
      <div className='ant-collapse-content-box'>
        <div>
          <div className='py-5px pl-20px'>
            {canRead ? (
              extraModules?.isChecked &&
              !extraModules?.isDeleted && (
                <ReportExtras
                  key={s.id}
                  snapshot={s}
                  report={s.report}
                  editable={(editableContent || editable) && !isFull}
                  extraFields={extraModules ? extraModules.fields : []}
                  refresh={refresh}
                />
              )
            ) : (
              <MosaicComponent />
            )}
          </div>
          <Droppable droppableId={selfId!} type={s.id} isDropDisabled={!editable}>
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                <ReportSnapshots
                  selfId={selfId}
                  reportId={reportId}
                  snapshots={children}
                  refresh={refresh}
                  editable={editable}
                  isShowKR={isShowKR}
                  isFull={isFull}
                  expandSnapshotIds={expandSnapshotIds}
                />
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      </div>
    </div>
  )

  return (
    <Draggable draggableId={s.id} index={index} isDragDisabled={!editable}>
      {(provided) => (
        <div
          className={cls('ant-collapse-item mb-8px', {
            'ant-collapse-item-active': !childCollapsed,
          })}
          ref={provided.innerRef}
          {...provided.draggableProps}
        >
          <div
            className={cls({
              'ml-12px': snapshot.depth > 1,
            })}
            id={s.id}
            onClick={(e) => {
              e.stopPropagation()
              changeCollapsed(s.id)
            }}
          >
            {header(provided)}
          </div>
          {content}
        </div>
      )}
    </Draggable>
  )
}

export const ReportSnapshots: React.FC<Omit<ReportSnapshotsProps, 'activeKeys' | 'setActiveKeys'>> = (props) => {
  const { t } = useTranslation()
  const { selfId, reportId, refresh, editable, snapshots, isShowKR, isFull, expandSnapshotIds } = props
  const [childrenCollapsed, setChildrenCollapsed] = React.useState<{ id: string; collapsed: boolean }[]>(
    snapshots.map((s) => ({
      id: s.id,
      collapsed: s.depth === 1 ? false : expandSnapshotIds.includes(s.id) ? false : true,
    }))
  )

  React.useEffect(() => {
    setChildrenCollapsed(
      snapshots.map((s) => ({
        id: s.id,
        collapsed: s.depth === 1 ? false : expandSnapshotIds.includes(s.id) ? false : true,
      }))
    )
  }, [expandSnapshotIds])

  const childrenCollapsedLen = childrenCollapsed.filter((c) => c.collapsed).length

  const changeCollapsed = (id: string) => {
    setChildrenCollapsed((prev) =>
      prev.map((item) => ({
        id: item.id,
        collapsed: item.id === id ? !item.collapsed : item.collapsed,
      }))
    )
  }

  const changeChildrenCollapsed = () => {
    setChildrenCollapsed((prev) => prev.map((item) => ({ id: item.id, collapsed: !childrenCollapsedLen })))
  }

  return (
    <div className='report-snapshots ant-collapse ant-collapse-ghost'>
      {snapshots.length !== 0 && (
        <div className='cursor-pointer space-x-10px mb-12px ml-12px' onClick={changeChildrenCollapsed}>
          <RightOutlined className='ant-collapse-arrow' rotate={childrenCollapsedLen ? undefined : -90} />
          <span className='font-bold'>{!childrenCollapsedLen ? t('收起全部项') : t('展开全部折叠项')}</span>
          {childrenCollapsedLen !== 0 && <span className='bg-light-500 p-2px text-12px'>+{childrenCollapsedLen}</span>}
        </div>
      )}
      {snapshots &&
        snapshots.map((snapshot, i) => {
          return (
            <React.Fragment key={`${snapshot.id}.${snapshot.depth}`}>
              {(snapshot.depth === 1 ||
                (childrenCollapsedLen !== childrenCollapsed.length && snapshot.depth !== 1)) && (
                <ReportSnapshot
                  index={i}
                  selfId={selfId}
                  reportId={reportId}
                  snapshots={snapshots}
                  childCollapsed={childrenCollapsed.find((item) => item.id === snapshot.id)?.collapsed || false}
                  snapshot={snapshot}
                  refresh={refresh}
                  editable={editable}
                  isShowKR={isShowKR}
                  isFull={isFull}
                  changeCollapsed={changeCollapsed}
                  expandSnapshotIds={expandSnapshotIds}
                />
              )}
            </React.Fragment>
          )
        })}
    </div>
  )
}
